/**
 * ReplicaDent — GA4 / GTM dataLayer tracking
 *
 * GTM rules:
 *   - All custom events are pushed via window.dataLayer with `event` field.
 *   - Configure GA4 Event tags with Custom Event triggers matching the names below.
 *   - DO NOT use Click URL / Page URL / Page View / Thank you page triggers in GTM
 *     for these events — they will produce duplicates.
 *   - GTM merges dataLayer keys across events. This file nulls stale params
 *     before each push so previous form_name / lead_type / page_type do not
 *     leak into later tags.
 *
 * Custom events emitted by this file:
 *   form_interaction, form_start, generate_lead, cta_click,
 *   popup_view, popup_click,
 *   contact_click,
 *   chat_open, chat_message,
 *   file_open, file_download,
 *   library_click, library_open,
 *   account_click, sign_up, login
 *
 * Standard Shopify ecommerce events (view_item, add_to_cart, purchase, …)
 * are NOT touched here — they are emitted by Shopify's native GA4 integration.
 */
(function () {
  window.dataLayer = window.dataLayer || [];

  /**
   * Events that should fire only once per page (e.g. form_interaction, form_start,
   * generate_lead, popup_view, login, sign_up).
   * Click events bypass this and always fire (one user click = one event).
   */
  const firedOnceKeys = new Set();

  /**
   * GTM merges consecutive dataLayer pushes. Keys from a previous event
   * (form_name, lead_type, page_type, …) would otherwise leak into the next
   * tag. Null them out before every custom event.
   */
  var DL_RESET_KEYS = [
    'form_name',
    'page_type',
    'lead_type',
    'tracking_method',
    'cta_name',
    'cta_text',
    'destination',
    'popup_name',
    'destination_url',
    'contact_type',
    'button_location',
    'chat_provider',
    'file_type',
    'file_name',
    'file_url',
    'library_action',
    'library_name',
    'library_category',
    'account_action',
    'method',
  ];

  function clearStaleParams() {
    var reset = {};
    for (var i = 0; i < DL_RESET_KEYS.length; i++) {
      reset[DL_RESET_KEYS[i]] = undefined;
    }
    window.dataLayer.push(reset);
  }

  function pushToDataLayer(payload) {
    clearStaleParams();
    window.dataLayer.push(payload);
  }

  function pushOnce(key, payload) {
    if (firedOnceKeys.has(key)) return;
    firedOnceKeys.add(key);
    pushToDataLayer(payload);
  }

  function pushEvent(payload) {
    pushToDataLayer(payload);
  }

  function pushOncePerSession(storageKey, payload) {
    try {
      if (sessionStorage.getItem(storageKey)) return;
      sessionStorage.setItem(storageKey, '1');
    } catch (_) {}
    pushEvent(payload);
  }

  function pageLocation() {
    return window.location.href;
  }

  function resolveUrl(el) {
    if (!el) return '';
    if (el.dataset && el.dataset.url) {
      try {
        return new URL(el.dataset.url, window.location.origin).href;
      } catch (_) {
        return el.dataset.url;
      }
    }
    return el.href || '';
  }

  function onClick(selector, handler) {
    document.addEventListener('click', function (e) {
      const target = e.target.closest(selector);
      if (!target) return;
      handler(target, e);
    });
  }

  /**
   * For same-tab `<a>` navigations we must give GTM time to flush the GA4 hit
   * before the page unloads. We preventDefault, push the event with a GTM
   * `eventCallback`, and fall back to a short timeout in case the trigger
   * doesn't wire the callback.
   *
   * Modifier-key / middle-click / target="_blank" cases are passed through —
   * the original tab/window stays alive long enough for a normal push.
   */
  function pushAndNavigate(el, e, payload) {
    if (!el || !e) {
      pushEvent(payload);
      return;
    }
    if (e.defaultPrevented) {
      pushEvent(payload);
      return;
    }
    if (el.tagName !== 'A' || !el.href) {
      pushEvent(payload);
      return;
    }
    if (
      el.target === '_blank' ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button === 1
    ) {
      pushEvent(payload);
      return;
    }

    e.preventDefault();
    const destination = el.href;
    let navigated = false;
    const go = function () {
      if (navigated) return;
      navigated = true;
      window.location.href = destination;
    };

    pushToDataLayer(
      Object.assign({ eventCallback: go, eventTimeout: 1200 }, payload)
    );
    setTimeout(go, 250);
  }

  // ---------------------------------------------------------------------------
  // 2.1 / 2.2 — Forms (form_interaction, form_start, generate_lead)
  //
  // form_interaction — first focus/click/input/change inside [data-form].
  // form_start       — at least one text-like field (not checkbox/radio) has a value.
  // generate_lead    — success UI only (Shopify Forms app step or native
  //                    contact form .form-status after server POST).
  // ---------------------------------------------------------------------------

  var SKIPPED_FIELD_TYPES = { hidden: 1, submit: 1, button: 1, reset: 1, image: 1 };
  var NON_START_FIELD_TYPES = { checkbox: 1, radio: 1, file: 1, color: 1, range: 1 };
  var OBSERVE_CONFIG = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-current-step'],
  };

  function walkShadowRoots(root, visit) {
    if (!root) return;
    visit(root);
    var elements = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (el.shadowRoot) {
        walkShadowRoots(el.shadowRoot, visit);
      }
      if (el.tagName === 'IFRAME') {
        try {
          var doc = el.contentDocument;
          if (doc && doc.documentElement) walkShadowRoots(doc, visit);
        } catch (_) {}
      }
    }
  }

  function queryDeep(root, selector) {
    var found = null;
    walkShadowRoots(root, function (node) {
      if (found) return;
      if (node.nodeType === 1 && node.matches && node.matches(selector)) {
        found = node;
        return;
      }
      if (node.querySelector) {
        found = node.querySelector(selector);
      }
    });
    return found;
  }

  function getFormFields(wrapper) {
    var fields = [];
    walkShadowRoots(wrapper, function (root) {
      var nodes = root.querySelectorAll
        ? root.querySelectorAll('input, textarea, select')
        : [];
      for (var i = 0; i < nodes.length; i++) {
        fields.push(nodes[i]);
      }
    });
    return fields.filter(function (field) {
      return !SKIPPED_FIELD_TYPES[field.type];
    });
  }

  function isStartField(field) {
    if (!field || !field.tagName) return false;
    var tag = field.tagName.toLowerCase();
    var type = (field.type || '').toLowerCase();
    if (SKIPPED_FIELD_TYPES[type] || NON_START_FIELD_TYPES[type]) return false;
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  }

  function isFieldFilled(field) {
    return (field.value || '').trim() !== '';
  }

  function hasFilledStartField(wrapper) {
    var fields = getFormFields(wrapper);
    for (var i = 0; i < fields.length; i++) {
      if (isStartField(fields[i]) && isFieldFilled(fields[i])) return true;
    }
    return false;
  }

  function resolveLeadType(formName) {
    if (/^offer_/i.test(formName)) return 'free_order';
    return 'form';
  }

  function getFormSuccessMethod(wrapper) {
    if (
      queryDeep(wrapper, '[data-current-step="success"]') ||
      queryDeep(wrapper, '#app-embed[data-current-step="success"]')
    ) {
      return 'success_step';
    }
    if (queryDeep(wrapper, '.form-status.form__message:not(.errors)')) {
      return 'server_success';
    }
    return null;
  }

  function pushGenerateLead(wrapper) {
    var formName = wrapper.dataset.form;
    var method = getFormSuccessMethod(wrapper);
    if (!method) return;

    var memoryKey = 'generate_lead::' + formName;
    if (firedOnceKeys.has(memoryKey)) return;

    if (method === 'server_success') {
      try {
        var storageKey = 'rd_generate_lead::' + formName;
        if (sessionStorage.getItem(storageKey)) {
          firedOnceKeys.add(memoryKey);
          return;
        }
        sessionStorage.setItem(storageKey, '1');
      } catch (_) {}
    }

    pushOnce(memoryKey, {
      event: 'generate_lead',
      form_name: formName,
      page_type: wrapper.dataset.pageType || undefined,
      lead_type: resolveLeadType(formName),
      tracking_method: method,
      page_location: pageLocation(),
    });
  }

  function watchFormSuccess(wrapper) {
    var observed = new WeakSet();

    function tryGenerateLead() {
      pushGenerateLead(wrapper);
      observeTree(wrapper);
    }

    function observeTree(root) {
      walkShadowRoots(root, function (node) {
        if (observed.has(node)) return;
        observed.add(node);
        try {
          observer.observe(node, OBSERVE_CONFIG);
        } catch (_) {}
      });

      var iframes = root.querySelectorAll ? root.querySelectorAll('iframe') : [];
      for (var i = 0; i < iframes.length; i++) {
        if (iframes[i].__rdLeadBound) continue;
        iframes[i].__rdLeadBound = true;
        iframes[i].addEventListener('load', tryGenerateLead);
      }
    }

    var observer = new MutationObserver(tryGenerateLead);

    tryGenerateLead();
  }

  function initForms() {
    var wrappers = document.querySelectorAll('[data-form]');
    if (!wrappers.length) return;

    wrappers.forEach(function (wrapper) {
      var formName = wrapper.dataset.form;
      var pageType = wrapper.dataset.pageType || undefined;

      ['focusin', 'click', 'input', 'change'].forEach(function (evt) {
        wrapper.addEventListener(
          evt,
          function () {
            pushOnce('form_interaction::' + formName, {
              event: 'form_interaction',
              form_name: formName,
              page_type: pageType,
              page_location: pageLocation(),
            });
          },
          true
        );
      });

      function maybeFormStart(e) {
        var field = e && e.target;
        if (field && field.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(field.tagName)) {
          if (!isStartField(field) || !isFieldFilled(field)) return;
        } else if (!hasFilledStartField(wrapper)) {
          return;
        }
        pushOnce('form_start::' + formName, {
          event: 'form_start',
          form_name: formName,
          page_type: pageType,
          page_location: pageLocation(),
        });
      }

      wrapper.addEventListener('input', maybeFormStart, true);
      wrapper.addEventListener('change', maybeFormStart, true);

      watchFormSuccess(wrapper);
    });
  }

  // ---------------------------------------------------------------------------
  // 2.3 — Landing CTA (cta_click)
  // ---------------------------------------------------------------------------
  function initCTA() {
    onClick('[data-cta]', (el) => {
      pushEvent({
        event: 'cta_click',
        cta_name: el.dataset.cta,
        cta_text: (el.innerText || el.textContent || '').trim(),
        destination: el.dataset.destination || 'form',
        page_type: el.dataset.pageType || undefined,
        page_location: pageLocation(),
      });
    });
  }

  // ---------------------------------------------------------------------------
  // 3 — Popup (popup_view, popup_click)
  // ---------------------------------------------------------------------------
  function initPopup() {
    // popup.js dispatches `popup:open` on window when the popup actually shows.
    window.addEventListener('popup:open', (e) => {
      const name = (e.detail && e.detail.name) || 'unknown_popup';
      pushOnce(`popup_view::${name}`, {
        event: 'popup_view',
        popup_name: name,
        page_location: pageLocation(),
      });
    });

    onClick('[data-popup-click]', (el) => {
      pushEvent({
        event: 'popup_click',
        popup_name: el.dataset.popupClick,
        destination_url: resolveUrl(el),
        page_location: pageLocation(),
      });
    });
  }

  // ---------------------------------------------------------------------------
  // 4 — Contacts: WhatsApp etc. (contact_click)
  // ---------------------------------------------------------------------------
  function initContacts() {
    onClick('[data-contact]', (el) => {
      pushEvent({
        event: 'contact_click',
        contact_type: el.dataset.contact,
        button_location: el.dataset.location || '',
        page_location: pageLocation(),
      });
    });
  }

  // ---------------------------------------------------------------------------
  // 5 — Chat (chat_open, chat_message)
  // Manual hooks — call from the chat widget integration.
  // ---------------------------------------------------------------------------
  function initChat() {
    window.trackChatOpen = function (provider) {
      pushEvent({
        event: 'chat_open',
        chat_provider: provider || 'chat_widget',
        page_location: pageLocation(),
      });
    };

    window.trackChatMessage = function (provider) {
      pushEvent({
        event: 'chat_message',
        chat_provider: provider || 'chat_widget',
        page_location: pageLocation(),
      });
    };
  }

  // ---------------------------------------------------------------------------
  // 6 — Catalog (file_open / file_download)
  //
  // We bind directly on each `[data-catalog]` element (not via document
  // delegation) and call `e.stopPropagation()` after pushing the event. This
  // short-circuits the bubble path that GTM auto-event Click/Link Click
  // triggers and GA4 Enhanced Measurement's outbound listener rely on at
  // `document` level, dampening the duplicate `click` / `Link Click` /
  // `outbound_click` hits. Triggers attached at `window` / capture phase will
  // still fire — those need to be turned off in GTM/GA4 settings.
  // ---------------------------------------------------------------------------
  function bindCatalogEl(el) {
    if (!el || el.__rdCatalogBound) return;
    el.__rdCatalogBound = true;

    el.addEventListener('click', function (e) {
      const fileUrl =
        el.tagName === 'A' && el.href ? el.href : el.dataset.catalogUrl || '';

      pushEvent({
        event: 'file_open',
        file_type: el.dataset.file || 'catalog',
        file_name: el.dataset.name || 'replicadent_catalog',
        file_url: fileUrl,
        button_location: el.dataset.location || '',
        page_location: pageLocation(),
      });

      if (el.dataset.fileDownload === 'true') {
        pushEvent({
          event: 'file_download',
          file_type: el.dataset.file || 'catalog',
          file_name: el.dataset.name || 'replicadent_catalog',
          file_url: fileUrl,
          page_location: pageLocation(),
        });
      }

      e.stopPropagation();
    });
  }

  function initCatalog() {
    document.querySelectorAll('[data-catalog]').forEach(bindCatalogEl);

    // Some catalog buttons live in sections that re-render dynamically
    // (header drawer, popup, theme-editor blocks). Watch the DOM for newly
    // inserted `[data-catalog]` nodes and bind them as they appear.
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes &&
          m.addedNodes.forEach(function (node) {
            if (!(node instanceof Element)) return;
            if (node.matches && node.matches('[data-catalog]')) {
              bindCatalogEl(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll('[data-catalog]').forEach(bindCatalogEl);
            }
          });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // 7 — Libraries
  //   7.1 library_click — top buttons "Download library guide" /
  //                       "How to use digital libraries" + nav links to libraries
  //   7.2 library_open  — Open buttons on individual libraries
  // ---------------------------------------------------------------------------
  function initLibraries() {
    onClick('[data-library-nav]', (el, e) => {
      pushAndNavigate(el, e, {
        event: 'library_click',
        library_action: el.dataset.libraryAction || 'open_libraries_page',
        library_name: el.dataset.libraryName || 'libraries_page',
        file_url: resolveUrl(el),
        button_location: el.dataset.location || '',
        page_location: pageLocation(),
      });
    });

    onClick('[data-library-open]', (el) => {
      pushEvent({
        event: 'library_open',
        library_name: el.dataset.libraryOpen,
        library_category: el.dataset.libraryCategory || 'digital_library',
        destination_url: resolveUrl(el),
        page_location: pageLocation(),
      });
    });

    autoWireLibrariesPage();
  }

  /**
   * On /pages/libraries we auto-wire library_open and library_click events
   * for content rendered by shared sections (multicolumn, image-banner) so we
   * don't have to hard-code data-attrs in templates that are reused elsewhere.
   */
  function autoWireLibrariesPage() {
    if (!/\/pages\/libraries(\/|$)/i.test(window.location.pathname)) return;

    document.querySelectorAll('.multicolumn-card').forEach((card) => {
      const titleEl = card.querySelector('.multicolumn-card__title');
      const link = card.querySelector('a.button, a.button--cta, a.button--tertiary');
      if (!titleEl || !link) return;
      if (link.dataset.libraryOpen || link.dataset.libraryNav) return;

      link.dataset.libraryOpen = (titleEl.textContent || '').trim();
      link.dataset.libraryCategory = 'digital_library';
    });

    const guideMap = [
      {
        match: /download[\s_-]*library[\s_-]*guide/i,
        action: 'download_library_guide',
        name: 'library_guide',
      },
      {
        match: /how[\s_-]*to[\s_-]*use[\s_-]*digital[\s_-]*librar/i,
        action: 'how_to_use_digital_libraries',
        name: 'digital_libraries_instruction',
      },
    ];

    document.querySelectorAll('.banner__buttons a, .banner__box a').forEach((link) => {
      if (link.dataset.libraryNav || link.dataset.libraryOpen) return;
      const label = (link.textContent || '').trim();
      const match = guideMap.find((g) => g.match.test(label));
      if (!match) return;
      link.dataset.libraryNav = '';
      link.dataset.libraryAction = match.action;
      link.dataset.libraryName = match.name;
      link.dataset.location = 'libraries_page';
    });
  }

  // ---------------------------------------------------------------------------
  // 8 — Account (account_click, sign_up, login)
  // ---------------------------------------------------------------------------
  function initAccount() {
    onClick('[data-account]', (el, e) => {
      pushAndNavigate(el, e, {
        event: 'account_click',
        account_action: el.dataset.account,
        page_location: pageLocation(),
      });
    });

    if (!window.Shopify || !Shopify.customer) return;

    const ref = (document.referrer || '').toLowerCase();

    if (/\/account\/register/.test(ref)) {
      pushOncePerSession('rd_signup_tracked', {
        event: 'sign_up',
        method: 'shopify_account',
        page_location: pageLocation(),
      });
    }

    if (/\/account\/login/.test(ref)) {
      pushOncePerSession('rd_login_tracked', {
        event: 'login',
        method: 'shopify_account',
        page_location: pageLocation(),
      });
    }
  }

  function init() {
    initForms();
    initCTA();
    initPopup();
    initContacts();
    initChat();
    initCatalog();
    initLibraries();
    initAccount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
